import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      
      // Check subscription
      try {
        const subResponse = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(subResponse.data);
      } catch (e) {
        console.error('Subscription check failed:', e);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signIn = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setUser(userData);
    
    // Check subscription after login
    try {
      const subResponse = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setSubscription(subResponse.data);
    } catch (e) {
      console.error('Subscription check failed:', e);
    }
    
    return userData;
  };

  const signUp = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  };

  const signOut = () => {
    localStorage.removeItem('token');
    setUser(null);
    setSubscription(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API}/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { headers: getAuthHeader() }
    );
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (token, newPassword) => {
    await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
  };

  const updateProfile = async (data) => {
    await axios.put(`${API}/users/${user.id}/profile`, null, {
      headers: getAuthHeader(),
      params: data
    });
    await checkAuth();
  };

  const checkSubscription = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeader()
      });
      setSubscription(response.data);
      return response.data;
    } catch (e) {
      console.error('Subscription check failed:', e);
      return null;
    }
  }, [user, getAuthHeader]);

  const value = {
    user,
    loading,
    subscription,
    signIn,
    signUp,
    signOut,
    changePassword,
    forgotPassword,
    resetPassword,
    updateProfile,
    checkSubscription,
    getAuthHeader,
    refreshUser: checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
